/**
 * Integration Hooks
 * 
 * Unified React hooks for bank integrations using the new /integrations/:provider API.
 * Replaces useBankIntegration with a provider-agnostic implementation.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { integrationService } from '../api'
import { useAuthStore } from '../store/auth'
import type { IntegrationStatus, IntegrationInfo, BankTransaction, BankAccount } from '../api'

// Date filter interface
export interface DateFilter {
  startDate: string
  endDate: string
}

/**
 * Hook for listing all available integrations
 */
export function useIntegrationsList() {
  const [integrations, setIntegrations] = useState<IntegrationInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchIntegrations = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const data = await integrationService.listIntegrations()
      setIntegrations(data)
    } catch {
      setError('Failed to fetch integrations')
      setIntegrations([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchIntegrations()
  }, [fetchIntegrations])

  return { integrations, loading, error, refetch: fetchIntegrations }
}

/**
 * Hook for fetching integration connection status
 */
export function useIntegrationStatus(provider: string) {
  const [status, setStatus] = useState<IntegrationStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { activeOrg } = useAuthStore()

  const fetchStatus = useCallback(async () => {
    if (!activeOrg?.id) return
    setLoading(true)
    setError(null)

    try {
      const data = await integrationService.getStatus(provider, activeOrg.id)
      setStatus(data)
    } catch {
      setStatus({ connected: false, service: provider })
      setError('Failed to fetch status')
    } finally {
      setLoading(false)
    }
  }, [provider, activeOrg?.id])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  return { status, loading, error, refetch: fetchStatus }
}

/**
 * Hook for fetching integration accounts
 */
export function useIntegrationAccounts(provider: string, enabled: boolean = true) {
  const [accounts, setAccounts] = useState<BankAccount[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { activeOrg } = useAuthStore()

  const fetchAccounts = useCallback(async () => {
    if (!enabled || !activeOrg?.id) return

    setLoading(true)
    setError(null)

    try {
      const data = await integrationService.getAccounts(provider, activeOrg.id)
      setAccounts(data)
    } catch {
      setError('Failed to fetch accounts')
      setAccounts([])
    } finally {
      setLoading(false)
    }
  }, [provider, enabled, activeOrg?.id])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  return { accounts, loading, error, refetch: fetchAccounts }
}

/**
 * Hook for fetching integration transactions
 */
export function useIntegrationTransactions(
  provider: string, 
  filters: DateFilter,
  options?: { accountId?: string; enabled?: boolean }
) {
  const [transactions, setTransactions] = useState<BankTransaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { activeOrg } = useAuthStore()

  const fetchTransactions = useCallback(async () => {
    if (options?.enabled === false || !activeOrg?.id) return

    setLoading(true)
    setError(null)

    try {
      const data = await integrationService.getTransactions(provider, activeOrg.id, {
        startDate: filters.startDate,
        endDate: filters.endDate,
        accountId: options?.accountId,
      })
      setTransactions(data)
    } catch {
      setError('Failed to fetch transactions')
      setTransactions([])
    } finally {
      setLoading(false)
    }
  }, [provider, activeOrg?.id, filters.startDate, filters.endDate, options?.accountId, options?.enabled])

  return { transactions, loading, error, refetch: fetchTransactions }
}

/**
 * Hook for OAuth connection flow
 */
export function useIntegrationConnect(provider: string, onSuccess?: () => void, connectOptions?: Record<string, string>) {
  const [isConnecting, setIsConnecting] = useState(false)
  const popupRef = useRef<Window | null>(null)
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null)
  const { activeOrg } = useAuthStore()

  const connect = useCallback(async () => {
    if (!activeOrg?.id) return
    setIsConnecting(true)

    try {
      const authUrl = await integrationService.getAuthUrl(provider, activeOrg.id, connectOptions)
      
      // Calculate popup position (centered)
      const width = 500
      const height = 600
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      
      // Open OAuth popup
      popupRef.current = window.open(
        authUrl,
        `${provider}-oauth`,
        `width=${width},height=${height},left=${left},top=${top}`
      )
      
      // Listen for message from popup
      messageHandlerRef.current = (event: MessageEvent) => {
        if (event.data?.type === `${provider}:connected` && event.data.success) {
          popupRef.current?.close()
          if (messageHandlerRef.current) {
            window.removeEventListener('message', messageHandlerRef.current)
          }
          setIsConnecting(false)
          onSuccess?.()
        }
      }
      
      window.addEventListener('message', messageHandlerRef.current)
      
      // Check if popup was closed manually
      const checkClosed = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(checkClosed)
          setIsConnecting(false)
          if (messageHandlerRef.current) {
            window.removeEventListener('message', messageHandlerRef.current)
          }
        }
      }, 500)
    } catch {
      setIsConnecting(false)
    }
  }, [provider, activeOrg?.id, onSuccess, connectOptions])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (messageHandlerRef.current) {
        window.removeEventListener('message', messageHandlerRef.current)
      }
      popupRef.current?.close()
    }
  }, [])

  return { connect, isConnecting }
}

/**
 * Hook for disconnecting an integration
 */
export function useIntegrationDisconnect(provider: string, onSuccess?: () => void) {
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const { activeOrg } = useAuthStore()

  const disconnect = useCallback(async () => {
    if (!activeOrg?.id) return
    setIsDisconnecting(true)

    try {
      await integrationService.disconnect(provider, activeOrg.id)
      onSuccess?.()
    } catch (err) {
      console.error('Disconnect failed:', err)
    } finally {
      setIsDisconnecting(false)
    }
  }, [provider, activeOrg?.id, onSuccess])

  return { disconnect, isDisconnecting }
}

/**
 * Hook for importing transactions
 */
export function useIntegrationImport(provider: string, onSuccess?: () => void) {
  const [isImporting, setIsImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const { activeOrg } = useAuthStore()

  const importTransactions = useCallback(async (transactionIds: string[]) => {
    if (transactionIds.length === 0 || !activeOrg?.id) return

    setIsImporting(true)
    setImportError(null)

    try {
      await integrationService.importTransactions(provider, activeOrg.id, transactionIds)
      onSuccess?.()
    } catch {
      setImportError('Import failed')
    } finally {
      setIsImporting(false)
    }
  }, [provider, activeOrg?.id, onSuccess])

  return { importTransactions, isImporting, importError }
}

/**
 * Combined hook for all integration functionality
 */
export function useIntegration(provider: string, connectOptions?: Record<string, string>) {
  const [filters, setFilters] = useState<DateFilter>({ startDate: '', endDate: '' })
  const [selectedAccountId, setSelectedAccountId] = useState<string>('')
  
  const { 
    status, 
    loading: statusLoading, 
    error: statusError, 
    refetch: refetchStatus 
  } = useIntegrationStatus(provider)
  
  const { 
    accounts,
    loading: accountsLoading,
    error: accountsError,
    refetch: refetchAccounts
  } = useIntegrationAccounts(provider, status?.connected ?? false)
  
  const { 
    transactions, 
    loading: transactionsLoading, 
    error: transactionsError, 
    refetch: refetchTransactions 
  } = useIntegrationTransactions(provider, filters, {
    accountId: selectedAccountId || undefined,
    enabled: status?.connected ?? false
  })
  
  const handleConnectSuccess = useCallback(() => {
    refetchStatus()
    refetchAccounts()
  }, [refetchStatus, refetchAccounts])
  
  const handleDisconnectSuccess = useCallback(() => {
    refetchStatus()
    setSelectedAccountId('')
  }, [refetchStatus])
  
  const handleImportSuccess = useCallback(() => {
    // Could refetch transactions here if needed
  }, [])
  
  const { connect, isConnecting } = useIntegrationConnect(provider, handleConnectSuccess, connectOptions)
  const { disconnect, isDisconnecting } = useIntegrationDisconnect(provider, handleDisconnectSuccess)
  const { importTransactions, isImporting, importError } = useIntegrationImport(provider, handleImportSuccess)
  
  return {
    // Status
    status,
    statusLoading,
    statusError,
    refetchStatus,
    
    // Accounts
    accounts,
    accountsLoading,
    accountsError,
    refetchAccounts,
    
    // Transactions
    transactions,
    transactionsLoading,
    transactionsError,
    refetchTransactions,
    
    // Filters
    filters,
    setFilters,
    selectedAccountId,
    setSelectedAccountId,
    
    // Actions
    connect,
    isConnecting,
    disconnect,
    isDisconnecting,
    importTransactions,
    isImporting,
    importError
  }
}
